import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

const ToastContext = createContext(null);
const DEFAULT_DURATION_MS = 5200;

const toneByType = {
  error: {
    icon: AlertCircle,
    container:
      'border-rose-200/90 bg-rose-50/95 text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/90 dark:text-rose-100',
    iconClass: 'text-rose-600 dark:text-rose-300'
  },
  success: {
    icon: CheckCircle2,
    container:
      'border-emerald-200/90 bg-emerald-50/95 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/90 dark:text-emerald-100',
    iconClass: 'text-emerald-600 dark:text-emerald-300'
  },
  info: {
    icon: Info,
    container:
      'border-border bg-white/95 text-text dark:border-slate-700/80 dark:bg-slate-950/95 dark:text-slate-100',
    iconClass: 'text-accent'
  }
};

const ToastItem = ({ toast, onDismiss }) => {
  const { t } = useI18n();
  const tone = toneByType[toast.type] || toneByType.info;
  const Icon = tone.icon;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={classNames(
        'toast-item pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur-md',
        tone.container
      )}
    >
      <Icon size={18} className={classNames('mt-0.5 shrink-0', tone.iconClass)} aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm font-medium leading-6">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg opacity-70 transition hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        aria-label={t('toastClose')}
      >
        <X size={16} />
      </button>
    </div>
  );
};

const ToastViewport = ({ toasts, onDismiss }) => {
  if (typeof document === 'undefined' || toasts.length === 0) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none fixed top-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-3"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);

    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, type = 'error', duration = DEFAULT_DURATION_MS }) => {
      const normalizedMessage = String(message || '').trim();

      if (!normalizedMessage) {
        return null;
      }

      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      setToasts((current) => [...current, { id, message: normalizedMessage, type }]);

      const timer = window.setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);

      return id;
    },
    [dismiss]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  const value = useMemo(
    () => ({
      showToast,
      dismiss,
      error: (message, options) => showToast({ message, type: 'error', ...options }),
      success: (message, options) => showToast({ message, type: 'success', ...options }),
      info: (message, options) => showToast({ message, type: 'info', ...options })
    }),
    [dismiss, showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within ToastProvider.');
  }

  return context;
};
